import React, { useRef, useEffect, useState, forwardRef, memo } from "react";
import styled from "@emotion/styled";
import { useGetResizer } from "../../../../contexts/Resizer";
import Card from "../Cover/Card";

import {
	useViewportScroll,
	motion,
	useTransform,
	useSpring,
} from "framer-motion";

import { fontSurt, h1, pSmall } from "../../../../styles/typography";
import { getTheme, grid, screen, spacing } from "../../../../styles/mixins";
import { easing } from "../../../../util/math";
import { useUIState } from "../../../../contexts/UIStore";

const transition = {
	duration: 0.5,
	ease: [0.43, 0.13, 0.23, 0.96],
};

const titleVariants = {
	exit: { opacity: 0, transition },
	enter: {
		opacity: 1,
		transition,
	},
};

const Container = styled.div`
	min-height: 100vh;
	width: 100%;
	position: relative;
	/* transform: translateZ(0px); */
`;

const HeightWrapper = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
`;

const Screen = styled.div`
	position: sticky;
	height: 100vh;
	top: 0;
	width: 100%;
`;

const Wrapper = styled.div`
	overflow: hidden;
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	${grid};
	text-align: center;
	align-items: center;
	justify-items: center;
`;

const TitleWrapper = styled(motion.div)`
	grid-column: 3 / -3;
	${spacing("marginTop", "l")}
	${spacing("marginBottom", "l")}

	@media ${screen("md")} {
		grid-column: 7 / -7;
	}
`;

const Title = styled.h1`
	${h1};
`;

const Line1 = styled(motion.span)`
	position: relative;
	z-index: -1;
	display: block;
	will-change: transform, opacity;

	span {
		${fontSurt};
		font-size: 0.99em;
		letter-spacing: -0.05em;
	}
`;

const Line2 = styled(motion.span)`
	position: relative;
	display: block;
	will-change: transform, opacity;

	span {
		${fontSurt};
		font-size: 0.99em;
		letter-spacing: -0.05em;
	}
`;

const Subtitle = styled(motion.p)`
	${pSmall};
	font-weight: 600;
	${spacing("marginBottom", "l")}
	will-change: transform, opacity;
`;

const Cards = styled(motion.div)`
	position: absolute;
	top: 55vh;
	left: calc((100vw - 118vw) * 0.5);
	width: 118vw;
	height: 123vw;
	z-index: 0;
	pointer-events: none;
	will-change: transform;

	@media ${screen("ml")} {
		top: 45vh;
		left: calc((100vw - 70vw) * 0.5);
		width: 70vw;
		height: 70vw;
	}

	@media ${screen("lg")} {
		top: 35vh;
		left: calc((100vw - 63vw) * 0.5);
		width: 63vw;
		height: 63vw;
	}
`;

const Cover = memo(
	forwardRef(function (
		{ titleLine1, titleLine2, subtitle, refTop, refBottom },
		ref
	) {
		const [windowHeight, setWindowHeight] = useState(768);
		const uiState = useUIState();
		useEffect(() => {
			const observer = new IntersectionObserver(
				(entries) => {
					entries.forEach((entry) => {
						if (entry.isIntersecting) {
							uiState.setSection("");
							uiState.addTheme(getTheme("light"), "part-1-cover");
						} else {
							uiState.removeTheme("part-1-cover");
						}
					});
				},
				{ rootMargin: "-50% 0px -50%" }
			);
			observer.observe(ref.current);
			return () => {
				observer.disconnect();
			};
		}, [uiState, ref]);

		const height = 2.5;

		const { scrollY } = useViewportScroll();

		const titleProgressY = useSpring(
			useTransform(
				scrollY,
				[refTop + (refBottom - refTop) * 0.55, refBottom],
				[0, -1 * (refTop + (refBottom - refTop) * 0.35)],
				{
					ease: easing.inQuad,
				}
			),
			{
				stiffness: 1000,
				damping: 50,
			}
		);

		const titleProgressOpacity = useSpring(
			useTransform(
				scrollY,
				[refTop + (refBottom - refTop) - windowHeight * 0.5, refBottom],
				[1, 0]
			),
			{
				stiffness: 1000,
				damping: 50,
			}
		);

		const cardsProgressY = useSpring(
			useTransform(
				scrollY,
				[
					refTop,
					refTop + (refBottom - refTop) * 0.55,
					refTop + (refBottom - refTop) * 0.65,
					refBottom + 3 * windowHeight,
				],
				[0, -0.4 * refBottom, -0.4 * refBottom, 0.2 * refBottom],
				{
					ease: easing.outQuad,
				}
			),
			{
				stiffness: 1000,
				damping: 50,
			}
		);

		useEffect(() => {
			const handleResize = () => {
				updateWindowHeight();
			};

			const updateWindowHeight = () => {
				setWindowHeight(window.innerHeight);
			};

			updateWindowHeight();
			window.addEventListener("resize", handleResize);

			return () => {
				window.removeEventListener("resize", handleResize);
			};
		}, []);

		return (
			<Container ref={ref} style={{ height: `${height * 100}vh` }}>
				<HeightWrapper style={{ height: `${height * 200}vh` }}>
					<Screen style={{ marginBottom: `${height * 100}vh` }}>
						<Wrapper
							style={{
								height: `${100 + height * 100}vh`,
								paddingBottom: `${height * 100}vh`,
							}}>
							<Cards style={{ y: cardsProgressY }}>
								<Card
									refTop={refTop}
									refBottom={refBottom + windowHeight}
									windowHeight={windowHeight}
									positionY={1}
									positionX={0}
									positionZ={0}
									rotation="right"
									speedY={0}
									speedX={20}
									rotationX={-20}
									rotationZ={-2}
									rotationY={12}
									delay={0.14}
								/>
								<Card
									refTop={refTop}
									refBottom={refBottom + windowHeight}
									windowHeight={windowHeight}
									positionY={1}
									positionX={1}
									positionZ={0}
									rotation="right"
									speedY={0}
									speedX={90}
									rotationX={-35}
									rotationY={-4}
									delay={0.13}
								/>
								<Card
									refTop={refTop}
									refBottom={refBottom + windowHeight}
									windowHeight={windowHeight}
									positionY={1}
									positionX={0}
									positionZ={0}
									rotation="left"
									speedY={0}
									speedX={20}
									rotationX={25}
									rotationY={5}
									delay={0.16}
								/>
								<Card
									refTop={refTop}
									refBottom={refBottom + windowHeight}
									windowHeight={windowHeight}
									positionY={1}
									positionX={0}
									positionZ={1}
									rotation="left"
									speedY={0}
									speedX={-80}
									rotationY={-30}
									rotationZ={30}
									delay={0.15}
								/>
								<Card
									refTop={refTop}
									refBottom={refBottom + windowHeight}
									windowHeight={windowHeight}
									positionY={1}
									positionX={0}
									positionZ={2}
									rotation="right"
									facingIn={true}
									speedY={0}
									speedX={-115}
									rotationX={11}
									delay={0.13}
									faceIn={true}
								/>
								<Card
									refTop={refTop}
									refBottom={refBottom + windowHeight}
									windowHeight={windowHeight}
									positionY={1}
									positionX={1}
									positionZ={2}
									rotation="right"
									facingIn={true}
									speedY={0}
									speedX={-60}
									rotationX={21}
									rotationY={10}
									delay={0.12}
									faceIn={true}
								/>
								<Card
									refTop={refTop}
									refBottom={refBottom + windowHeight}
									windowHeight={windowHeight}
									positionY={1}
									positionX={2}
									positionZ={0}
									rotation="left"
									speedY={0}
									speedX={20}
									rotationX={-8}
									rotationY={15}
									delay={0.09}
									faceIn={true}
								/>
								<Card
									refTop={refTop}
									refBottom={refBottom + windowHeight}
									windowHeight={windowHeight}
									positionY={1}
									positionX={2}
									positionZ={1}
									rotation="left"
									speedY={0}
									speedX={40}
									rotationX={-18}
									rotationY={-10}
									delay={0.08}
									faceIn={true}
								/>
								<Card
									refTop={refTop}
									refBottom={refBottom + windowHeight}
									windowHeight={windowHeight}
									positionY={2}
									positionX={0}
									positionZ={0}
									speedY={0}
									speedX={5}
									rotationX={15}
									rotationY={14}
									rotation="right"
									angle="back"
									delay={0.07}
								/>
								<Card
									refTop={refTop}
									refBottom={refBottom + windowHeight}
									windowHeight={windowHeight}
									positionY={2}
									positionX={0}
									positionZ={2}
									speedY={0}
									speedX={-200}
									rotationX={45}
									rotationZ={5}
									rotation="right"
									angle="front"
									delay={0.08}
									faceIn={true}
								/>
								<Card
									refTop={refTop}
									refBottom={refBottom + windowHeight}
									windowHeight={windowHeight}
									positionY={2}
									positionX={1}
									positionZ={0}
									speedY={0}
									speedX={50}
									rotationX={10}
									rotationZ={15}
									rotation="right"
									angle="back"
									delay={0.03}
								/>
								<Card
									refTop={refTop}
									refBottom={refBottom + windowHeight}
									windowHeight={windowHeight}
									positionY={2}
									positionX={1}
									positionZ={2}
									speedY={0}
									speedX={-100}
									rotationX={30}
									rotationZ={14}
									rotation="right"
									angle="front"
									delay={0.05}
									faceIn={true}
								/>
							</Cards>
							<TitleWrapper>
								<Subtitle
									variants={titleVariants}
									dangerouslySetInnerHTML={{ __html: subtitle.toUpperCase() }}
									style={{ y: titleProgressY, opacity: titleProgressOpacity }}
								/>
								<Title variants={titleVariants}>
									<Line1
										dangerouslySetInnerHTML={{ __html: titleLine1 }}
										style={{ y: titleProgressY, opacity: titleProgressOpacity }}
									/>
									<Line2
										dangerouslySetInnerHTML={{ __html: titleLine2 }}
										style={{ y: titleProgressY, opacity: titleProgressOpacity }}
									/>
								</Title>
							</TitleWrapper>
						</Wrapper>
					</Screen>
				</HeightWrapper>
			</Container>
		);
	})
);

export default function CoverArea(props) {
	const ref = useRef();
	const area = useGetResizer();
	const [refTop, setRefTop] = useState(0);
	const [refBottom, setRefBottom] = useState(1);
	useEffect(() => {
		const refPosition = ref.current?.getBoundingClientRect();
		const top = refPosition.top + window.pageYOffset;
		const bottom = top + refPosition.height - window.innerHeight;

		setRefTop(top);
		setRefBottom(bottom);
	}, [area]);
	return <Cover {...props} ref={ref} refTop={refTop} refBottom={refBottom} />;
}
